import apiClient from './api';
// Types based on OpenAPI specification
export interface Notification {
  id: number;
  userId: number;
  type: 'system' | 'property_update' | 'inspection_reminder' | 'invoice_due' | 'message';
  title: string;
  message: string;
  data?: unknown;
  priority: 'low' | 'normal' | 'high' | 'urgent';
  read: boolean;
  readAt?: string;
  createdAt: string;
}

export interface CreateNotificationDto {
  userId: number;
  type: 'system' | 'property_update' | 'inspection_reminder' | 'invoice_due' | 'message';
  title: string;
  message: string;
  data?: unknown;
  priority?: 'low' | 'normal' | 'high' | 'urgent';
}

export interface NotificationsResponse {
  notifications: Notification[];
  total: number;
  unread: number;
  page: number;
  totalPages: number;
}

export interface NotificationPreferences {
  userId: number;
  emailEnabled: boolean;
  pushEnabled: boolean;
  smsEnabled: boolean;
  types: {
    system: boolean;
    property_update: boolean;
    inspection_reminder: boolean;
    invoice_due: boolean;
    message: boolean;
  };
}

export interface BroadcastNotificationDto {
  type: 'system' | 'property_update' | 'inspection_reminder' | 'invoice_due' | 'message';
  title: string;
  message: string;
  data?: unknown;
  priority?: 'low' | 'normal' | 'high' | 'urgent';
  targetRoles?: ('admin' | 'agent' | 'client')[];
}

export type DeliveryStatus = 'PENDING' | 'SENT' | 'DELIVERED' | 'FAILED' | 'DEAD_LETTERED';
export type DeliveryChannel = 'IN_APP' | 'EMAIL' | 'WHATSAPP' | 'SMS';

export interface DeliveryLog {
  id: number;
  channel: DeliveryChannel;
  status: DeliveryStatus;
  externalId?: string;
  attemptCount: number;
  lastAttemptAt?: string;
  deliveredAt?: string;
  error?: string;
  createdAt: string;
  notification: {
    id: number;
    title: string;
    message: string;
    type: string;
    user: { id: number; email: string; firstName?: string; lastName?: string };
    organisation?: { id: number; name: string } | null;
  };
}

export interface DeliveryLogsResponse {
  logs: DeliveryLog[];
  total: number;
  page: number;
  totalPages: number;
}

export interface NotificationFilters {
  type?: 'system' | 'property_update' | 'inspection_reminder' | 'invoice_due' | 'message';
  isRead?: boolean;
  propertyId?: number;
  page?: number;
  limit?: number;
}

function buildParams(filters?: Record<string, unknown>): string {
  if (!filters) return '';
  const params = new URLSearchParams();
  Object.entries(filters).forEach(([key, value]) => {
    if (value !== undefined && value !== null) {
      params.append(key, String(value));
    }
  });
  return params.toString();
}

export const notificationsService = {
  async createNotification(notificationData: CreateNotificationDto): Promise<Notification> {
    const { data } = await apiClient.post<Notification>('/notifications', notificationData);
    return data;
  },

  async getNotifications(filters?: NotificationFilters): Promise<NotificationsResponse> {
    const { data } = await apiClient.get<NotificationsResponse>(
      `/notifications?${buildParams(filters)}`
    );
    return data;
  },

  async getPreferences(): Promise<NotificationPreferences> {
    const { data } = await apiClient.get<NotificationPreferences>('/notifications/preferences');
    return data;
  },

  async updatePreferences(preferences: NotificationPreferences): Promise<NotificationPreferences> {
    const { data } = await apiClient.patch<NotificationPreferences>(
      '/notifications/preferences',
      preferences
    );
    return data;
  },

  async getUnreadCount(): Promise<{ unreadCount: number }> {
    const { data } = await apiClient.get<{ unreadCount: number }>('/notifications/unread-count');
    return data;
  },

  async markAsRead(id: number): Promise<Notification> {
    const { data } = await apiClient.patch<Notification>(`/notifications/${id}/read`);
    return data;
  },

  async markAllAsRead(): Promise<{ message: string }> {
    const { data } = await apiClient.post<{ message: string }>('/notifications/mark-all-read');
    return data;
  },

  async deleteNotification(id: number): Promise<void> {
    await apiClient.delete(`/notifications/${id}`);
  },

  async broadcastNotification(broadcastData: BroadcastNotificationDto): Promise<void> {
    await apiClient.post('/notifications/broadcast', broadcastData);
  },

  // ── SUPER_ADMIN delivery audit ────────────────────────────────────────────

  async getDeliveryLogs(params?: {
    page?: number;
    limit?: number;
    status?: string;
    channel?: string;
  }): Promise<DeliveryLogsResponse> {
    const { data } = await apiClient.get<DeliveryLogsResponse>(
      '/notifications/admin/delivery-logs',
      { params }
    );
    return data;
  },

  async resendDelivery(logId: number): Promise<{ queued: boolean }> {
    const { data } = await apiClient.post<{ queued: boolean }>(
      `/notifications/admin/delivery-logs/${logId}/resend`
    );
    return data;
  },
};
